/**
 * tool-labels
 *
 * Single source of truth for tool display names used in tool activity log,
 * message loading state, and annotation response time. For web search, label
 * is dynamic: "Searched \"query\"" when result has query.
 *
 * Primary responsibilities:
 * - Map tool annotation keys to display labels
 * - Provide getToolDisplayLabel(key, result?) for dynamic labels (e.g. web search)
 */

import { PLATFORM_TOOL_LABELS } from '@/lib/platformTools/utils/platformToolLabelsData'
import { getRoutedLosiSpacesToolKey } from '@/lib/spaces/losi-spaces-tool-routing'

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Display labels for known tool annotation keys (static fallback). */
export const TOOL_LABELS: Record<string, string> = {
  MemoryTool__storeMemoryToRemember: 'Memory',
  MemoryTool__listMemories: 'Memories',
  MemoryTool__updateMemory: 'Memory',
  MemoryTool__deleteMemory: 'Memory',
  LosiSearch__searchEverything: 'Losi search',
  LosiUndo__listUndoableActions: 'Undoable changes',
  LosiUndo__undoAction: 'Undo change',
  CredentialVault__listCredentials: 'Credential vault',
  CredentialVault__requestLogin: 'Private vault login',
  computerUse__requestCredential: 'Private vault login',
  ChatSearch__searchChats: 'Chat search',
  BookmarkSearch__searchBookmarks: 'Bookmarks',
  Sidecar__delegate: 'Sidecar',
  BrowserExtensionHandoffTool__listDevices: 'Browser Sidecar devices',
  BrowserExtensionHandoffTool__handoffTask: 'Browser Sidecar',
  BrowserExtensionHandoffTool__getTaskStatus: 'Browser Sidecar status',
  StarredMessagesSearch__searchStarredMessages: 'Starred messages',
  ChatManagementTool__getChats: 'Chats',
  ChatManagementTool__getChatMessages: 'Chat messages',
  ChatManagementTool__pinChat: 'Pin chat',
  ChatManagementTool__starMessage: 'Star message',
  ConnectedServices__listConnectedExtensions: 'Connected accounts',
  ConnectedServices__listActionsForExtension: 'Actions',
  ConnectedServices__executeExtensionAction: 'Execute action',
  ConnectedServices__checkToolkitConnection: 'Toolkit connection',
  ToolManagementTool__enableTool: 'Enable Tool',
  ToolManagementTool__disableTool: 'Disable Tool',
  ToolManagementTool__listAvailableTools: 'Available Tools',
  unifiedImage__imageGenerationViaunifiedImage: 'Image',
  unifiedImage__imageEditViaunifiedImage: 'Image edit',
  webScraper__FetchDataFromUrl: 'Fetch URL',
  webScraper__googleSearch: 'Web search',
  webScraper__youtubeCaptions: 'YouTube',
  webScraper__getWeather: 'Weather',
  webScraper__renderManimAnimation: 'Animation',
  webScraper__manimAnimation: 'Animation',
  taskAutomation__createTask: 'Task creation',
  taskAutomation__listTasks: 'Task list',
  taskAutomation__runTask: 'Task execution',
  taskAutomation__scheduleTask: 'Task scheduling',
  yahooFinance__get_stock_data: 'Finance',
  currentTime__getCurrentTime: 'Time',
  ContentEngine__createCampaignDraft: 'Campaign draft',
  ContentEngine__approveCampaignAndCreateAssetPack: 'Asset pack',
  ContentEngine__approveAssetPackAndGenerateVisuals: 'Campaign visuals',
  ContentEngine__linkResultToSpace: 'Engine output',
  ContentEngine__getContext: 'Content Engine context',
  ContentEngine__updateCampaign: 'Campaign update',
  LosiProductCreation__createSite: 'Creating Site',
  LosiProductCreation__createEngine: 'Creating Engine',
  deepResearch: 'Deep research',
  platformTools: 'Platform tools',
  nexus_crm: 'Contacts',
  nexus_booking: 'Booking',
  NexusCRMTool__run: 'Contacts',
  audioTool__processAudio: 'Audio',
  threeDAssetGenerator__generate3DAsset: '3D asset',
  show_plan: 'Updated plan',
  set_todos: 'Updated steps',
  update_todo_status: 'Updated step status',
  edit_canvas: 'Edited canvas',
  rename_chat: 'Renamed chat',
  imageGenerator: 'Image',
  gptImageFull: 'Image',
  flux1Pro: 'Image',
  stableDiffusion: 'Image',
  videoGenerator: 'Video',
  studioVideoGenerator: 'Video',
  memoryTool: 'Memory',
  vectorScratchpadTool: 'Vector memory',
  deepResearchPlatformTool: 'Research',
  manimAnimation: 'Animation',
  VirtualFilesystem__losi__fs_list_files: 'Virtual files',
  VirtualFilesystem__losi__fs_read_file: 'Virtual files',
  VirtualFilesystem__losi__fs_write_file: 'Virtual files',
  VirtualFilesystem__losi__fs_patch_file: 'Virtual files',
  losiSpaces__spaceTool: 'Spaces',
  losiSpaces__spaceTaskTool: 'Space tasks',
  losiSpaces__spaceNoteTool: 'Space notes',
  losiSpaces__spaceDesignTool: 'Space designs',
  losiSpaces__spaceSheetTool: 'Space sheets',
  losiSpaces__spaceFolderTool: 'Space folders',
  losiSpaces__spaceSprintTool: 'Space sprints',
  losiSpaces__spaceCalendarTool: 'Space calendars',
  losiSpaces__spaceContextTool: 'Space context',
  losiSpaces__spaceMemberTool: 'Space members',
  losiSpaces__spaceChatTool: 'Space chats',
  losiSpaces__spaceConversationTool: 'Space conversations',
  losiSpaces__spaceWhiteboardTool: 'Space whiteboards',
  losiSpaces__listSpaces: 'Listed Spaces',
  losiSpaces__createSpace: 'Created Space',
  losiSpaces__updateSpace: 'Updated Space',
  losiSpaces__archiveSpace: 'Archived Space',
  losiSpaces__listSpaceTasks: 'Listed tasks',
  losiSpaces__getSpaceTask: 'Read task',
  losiSpaces__createSpaceTask: 'Created task',
  losiSpaces__updateSpaceTask: 'Updated task',
  losiSpaces__deleteSpaceTask: 'Deleted task',
  losiSpaces__listTaskSubtasks: 'Listed subtasks',
  losiSpaces__createTaskSubtask: 'Created subtask',
  losiSpaces__updateTaskSubtask: 'Updated subtask',
  losiSpaces__deleteTaskSubtask: 'Deleted subtask',
  losiSpaces__listTaskChecklistItems: 'Listed checklist',
  losiSpaces__createTaskChecklistItem: 'Created checklist item',
  losiSpaces__updateTaskChecklistItem: 'Updated checklist item',
  losiSpaces__deleteTaskChecklistItem: 'Deleted checklist item',
  losiSpaces__listTaskNotes: 'Listed task notes',
  losiSpaces__deleteTaskNote: 'Deleted task note',
  losiSpaces__listTaskLinkedItems: 'Listed linked items',
  losiSpaces__searchTaskAttachableItems: 'Searched attachable items',
  losiSpaces__attachTaskLinkedItem: 'Attached item',
  losiSpaces__removeTaskLinkedItem: 'Removed item',
  losiSpaces__listSpaceNotes: 'Listed notes',
  losiSpaces__getSpaceNote: 'Read note',
  losiSpaces__createSpaceNote: 'Created note',
  losiSpaces__updateSpaceNote: 'Updated note',
  losiSpaces__deleteSpaceNote: 'Deleted note',
  losiSpaces__listSpaceFolders: 'Listed folders',
  losiSpaces__getSpaceFolder: 'Opened folder',
  losiSpaces__createSpaceFolder: 'Created folder',
  losiSpaces__updateSpaceFolder: 'Updated folder',
  losiSpaces__listSpaceSheets: 'Listed sheets',
  losiSpaces__createSpaceSheet: 'Created sheet',
  losiSpaces__updateSpaceSheet: 'Updated sheet',
  losiSpaces__deleteSpaceSheet: 'Deleted sheet',
  losiSpaces__replaceSpaceSheetWorkbook: 'Replaced sheet workbook',
  losiSpaces__applySpaceSheetRangeUpdate: 'Updated sheet range',
  losiSpaces__applySpaceSheetRangeFill: 'Updated sheet range',
  losiSpaces__applySpaceSheetRangeTransfer: 'Updated sheet range transfer',
  losiSpaces__applySpaceSheetStructureUpdate: 'Updated sheet structure',
  losiSpaces__applySpaceSheetDimensionUpdate: 'Updated sheet dimensions',
  losiSpaces__applySpaceSheetVisibilityUpdate: 'Updated sheet visibility',
  losiSpaces__listSpaceSprints: 'Listed sprints',
  losiSpaces__getSpaceSprint: 'Opened sprint',
  losiSpaces__createSpaceSprint: 'Created sprint',
  losiSpaces__updateSpaceSprint: 'Updated sprint',
  losiSpaces__listSpaceEvents: 'Listed events',
  losiSpaces__getSpaceEvent: 'Opened event',
  losiSpaces__createSpaceEvent: 'Created event',
  losiSpaces__updateSpaceEvent: 'Updated event',
  losiSpaces__listManagedCalendars: 'Listed managed calendars',
  losiSpaces__listManagedCalendarEvents: 'Listed managed calendar events',
  losiSpaces__createManagedCalendarEvent: 'Created managed event',
  losiSpaces__updateManagedCalendarEvent: 'Updated managed event',
  losiSpaces__deleteManagedCalendarEvent: 'Deleted managed event',
  losiSpaces__checkManagedCalendarAvailability: 'Checked calendar availability',
  losiSpaces__listSpaceFiles: 'Listed files',
  losiSpaces__getSpaceFile: 'Opened file',
  losiSpaces__searchSpaceFiles: 'Searched files',
  losiSpaces__attachSpaceFile: 'Attached file',
  losiSpaces__updateSpaceFile: 'Updated file',
  losiSpaces__detachSpaceFile: 'Detached file',
  losiSpaces__listSpaceCollections: 'Listed collections',
  losiSpaces__getSpaceCollection: 'Opened collection',
  losiSpaces__attachSpaceCollection: 'Attached collection',
  losiSpaces__updateSpaceCollection: 'Updated collection',
  losiSpaces__detachSpaceCollection: 'Detached collection',
  losiSpaces__listSpaceMembers: 'Listed members',
  losiSpaces__listSpaceMemberDirectory: 'Listed member directory',
  losiSpaces__addSpaceMember: 'Added Space member',
  losiSpaces__inviteToSpace: 'Invited to Space',
  losiSpaces__updateSpaceMemberRole: 'Updated member role',
  losiSpaces__removeSpaceMember: 'Removed member',
  losiSpaces__listSpaceChats: 'Listed chats',
  losiSpaces__getSpaceChat: 'Opened chat',
  losiSpaces__linkSpaceChat: 'Linked chat',
  losiSpaces__moveSpaceChat: 'Moved chat',
  losiSpaces__unlinkSpaceChat: 'Unlinked chat',
  losiSpaces__listSpaceConversations: 'Listed conversations',
  losiSpaces__startSpaceConversation: 'Created conversation',
  losiSpaces__getSpaceConversation: 'Opened conversation',
  losiSpaces__updateSpaceConversation: 'Updated conversation',
  losiSpaces__archiveSpaceConversation: 'Archived conversation',
  losiSpaces__duplicateSpaceConversation: 'Duplicated conversation',
  losiSpaces__listSpaceConversationMessages: 'Listed conversation messages',
  losiSpaces__respondSpaceConversation: 'Replied in conversation',
  losiSpaces__listSpaceWhiteboards: 'Listed whiteboards',
  losiSpaces__getSpaceWhiteboard: 'Opened whiteboard',
  losiSpaces__createSpaceWhiteboard: 'Created whiteboard',
  losiSpaces__updateSpaceWhiteboard: 'Updated whiteboard',
  losiSpaces__deleteSpaceWhiteboard: 'Deleted whiteboard',
  losiSpaces__listSpaceDesigns: 'Listed designs',
  losiSpaces__getSpaceDesign: 'Opened design',
  losiSpaces__createSpaceDesign: 'Created design',
  losiSpaces__updateSpaceDesign: 'Updated design',
  losiSpaces__deleteSpaceDesign: 'Deleted design',
  losiSpaces__applySpaceDesignSceneMutation: 'Updated design scene'
}

/**
 * Get display label for a tool. For webScraper__googleSearch, when result has
 * query/searchParameters.q, returns: Searched "query". Otherwise returns
 * TOOL_LABELS[key] or a formatted key.
 */
export function getToolDisplayLabel(key: string, result?: unknown): string {
  const routedSpacesKey = getRoutedLosiSpacesToolKey(key, result)
  if (routedSpacesKey && routedSpacesKey !== key) {
    return getToolDisplayLabel(routedSpacesKey, result)
  }

  if (key.startsWith('MCPIntegrationTool__') && result != null) {
    const raw =
      typeof result === 'object' && result !== null && 'result' in result
        ? (result as { result?: unknown }).result
        : result
    const obj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null
    const integration =
      obj?.integration && typeof obj.integration === 'object'
        ? (obj.integration as Record<string, unknown>)
        : null
    const name =
      typeof integration?.name === 'string'
        ? integration.name
        : typeof obj?.serverName === 'string'
          ? obj.serverName
          : null

    if (key.endsWith('__listMcpServerTools')) {
      return name ? `Listed MCP tools for "${name}"` : 'Listed MCP tools'
    }

    if (key.endsWith('__callMcpServerTool')) {
      return name ? `Ran MCP action on "${name}"` : 'Ran MCP action'
    }
  }

  if (key.startsWith('SkillIntegrationTool__') && result != null) {
    const raw =
      typeof result === 'object' && result !== null && 'result' in result
        ? (result as { result?: unknown }).result
        : result
    const obj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null
    const skill =
      obj?.skill && typeof obj.skill === 'object' ? (obj.skill as Record<string, unknown>) : null
    const name =
      typeof skill?.displayName === 'string'
        ? skill.displayName
        : typeof skill?.name === 'string'
          ? skill.name
          : null

    if (key.endsWith('__runSkill') || key.endsWith('__getSkillInstructions')) {
      return name ? `Loaded skill "${name}"` : 'Loaded skill'
    }
  }

  if (key === 'webScraper__googleSearch' && result != null) {
    const raw =
      typeof result === 'object' && result !== null && 'result' in result
        ? (result as { result?: unknown }).result
        : result
    const obj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null
    const query =
      typeof obj?.query === 'string'
        ? obj.query
        : typeof obj?.searchParameters?.q === 'string'
          ? (obj.searchParameters as { q: string }).q
          : null
    if (query) return `Searched "${query}"`
  }

  // Nexus tools: derive label from result action/entity fields
  if ((key === 'nexus_crm' || key === 'nexus_booking' || key === 'NexusCRMTool__run') && result != null) {
    const raw =
      typeof result === 'object' && result !== null && 'result' in result
        ? (result as { result?: unknown }).result
        : result
    const obj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null
    if (obj) {
      const action = typeof obj.action === 'string' ? obj.action : ''
      const entity = typeof obj.entity === 'string' ? obj.entity : ''
      const count = typeof obj.count === 'number' ? obj.count : null
      if (action && entity) {
        const label = action.replace(/_/g, ' ')
        const entityLabel = entity.replace(/_/g, ' ')
        if (count !== null) return `${capitalize(label)} — ${count} ${entityLabel}${count !== 1 ? 's' : ''}`
        return `${capitalize(label)} ${entityLabel}`
      }
      if (action) {
        return capitalize(action.replace(/_/g, ' '))
      }
    }
  }

  if (TOOL_LABELS[key]) return TOOL_LABELS[key]
  if (PLATFORM_TOOL_LABELS[key]) return PLATFORM_TOOL_LABELS[key]
  const parts = key.split('__')
  return parts.map((p) => p.replace(/([A-Z])/g, ' $1').trim()).join(' — ') || key
}
